import dayjs from 'dayjs';
import localforage from 'localforage';
import { chunk, omit, values } from 'lodash-es';
import { Deferred, sleep } from '../async';
import { LRUCache } from '../utils/lru-cache';

type UrlType = 'image' | 'download';
const SAVE_KEY = 'oss_images';
const MAX_QUEUE = 5;
interface OSSInfo {
  url: string;
  expireTime: number;
}

interface FetchUrlParams {
  blockId: string;
  ossName: string;
  download?: string;
  shareId?: string;
  isPublic?: boolean;
}

interface CreateUrlsResult {
  url: string;
  ttl: number;
}

interface Options {
  shareId?: string;
  isShare?: boolean;
}

interface Props {
  request: any;
  getIsShare: () => boolean;
}
/**
 * 通过oss name拿到对应的图片url/下载地址
 */
export class UrlFetcher {
  private lruCache;
  private paddingFetchUrl: Record<
    string,
    FetchUrlParams & { callback: (res?: CreateUrlsResult) => void }
  >;
  private fetchers: Record<string, Promise<string>> = {};
  private padding: boolean;
  private isShare: boolean;
  private request: any;

  constructor(props: Props) {
    const { request, getIsShare } = props;
    this.lruCache = new LRUCache<string, OSSInfo>(localforage, SAVE_KEY, 1000);
    this.paddingFetchUrl = {};
    this.padding = false;
    this.isShare = getIsShare();
    this.request = request;
    setInterval(() => {
      this.cleanup();
    }, 1000 * 60);
  }

  private async getUrl() {
    if (this.padding) return;
    this.padding = true;

    // 短时间收集所有请求。然后批量发出去
    await sleep(500);
    this.padding = false;

    const urlChunk = chunk(values(this.paddingFetchUrl), MAX_QUEUE);
    for (const valuesPaddingFetchUrl of urlChunk) {
      if (!valuesPaddingFetchUrl.length) return;

      this.paddingFetchUrl = {};

      const res = await this.request.infra.createUrls.raw({
        batch: valuesPaddingFetchUrl.map((i) => omit(i, 'callback')),
      });

      if (res.code === 200) {
        // @ts-ignore idl
        res.data.forEach((item, index) => {
          if (typeof item !== 'string') {
            valuesPaddingFetchUrl[index]?.callback(item);
          } else {
            valuesPaddingFetchUrl[index]?.callback();
          }
        });
      } else {
        valuesPaddingFetchUrl.forEach((item) => {
          item.callback();
        });
      }
      if (values(this.paddingFetchUrl).length) {
        void this.getUrl();
      }
    }
  }

  private async addPaddingFetchUrl(key: string, params: FetchUrlParams) {
    const deferred = new Deferred<CreateUrlsResult>();
    let rerunNum = 0;

    const _run = () => {
      // 超时
      const timer = setTimeout(() => {
        clearTimeout(timer);
        if (rerunNum > 2) {
          deferred.resolve({ url: '', ttl: 0 });
          return;
        }
        rerunNum++;
        _run();
      }, 1000 * 5);

      this.paddingFetchUrl[key] = {
        ...params,
        callback: (res) => {
          clearTimeout(timer);
          if (res) {
            deferred.resolve(res);
          } else {
            deferred.resolve({ url: '', ttl: 0 });
          }
        },
      };

      void this.getUrl();
    };

    _run();

    return deferred.promise;
  }

  private async fetchUrl(
    type: UrlType,
    blockId: string,
    ossName: string,
    fileName?: string,
    opt?: Options
  ) {
    const { shareId, isShare } = opt || {};
    if (!ossName) return '';
    const cacheKey = this.getCacheKey(type, ossName, fileName, !!(isShare || shareId));
    this.tryClearCache(cacheKey);
    const ret = this.lruCache.get(cacheKey);
    if (ret) {
      return ret.url;
    }

    const cacheFetcher = this.fetchers[cacheKey];
    if (cacheFetcher) {
      return cacheFetcher;
    }

    const task = async () => {
      const res = await this.addPaddingFetchUrl(cacheKey, {
        blockId,
        ossName,
        download: fileName,
        shareId,
        isPublic: Boolean(isShare || this.isShare),
      });

      delete this.fetchers[cacheKey];
      if (!res.url) {
        return '';
      }

      this.lruCache.put(cacheKey, {
        url: res.url,
        // 经常失效，所以先固定有效期
        expireTime: dayjs().add(30, 'minutes').valueOf(),
      });

      return res.url;
    };

    const taskRun = task();
    this.fetchers[cacheKey] = taskRun;

    return taskRun;
  }

  putCache(ossName: string, url: string, expireTime: number) {
    const cacheKey1 = this.getCacheKey('image', ossName);
    const time = expireTime - 1000 * 30 * 10;
    this.lruCache.put(cacheKey1, {
      url,
      expireTime: time,
    });
    const cacheKey2 = this.getCacheKey('download', ossName);
    this.lruCache.put(cacheKey2, {
      url,
      expireTime: time,
    });
  }

  /**
   * 获取图片地址,一般情况下推荐你使用useResource来获取地址
   */
  async fetchImageUrl(params: {
    blockId: string;
    ossName: string;
    fileName?: string;
    opt?: Options;
  }) {
    return this.fetchUrl('image', params.blockId, params.ossName, params.fileName, params.opt);
  }

  /**
   * 获取下载地址
   */
  async fetchDownloadUrl(params: {
    blockId: string;
    ossName: string;
    fileName?: string;
    opt?: Options;
  }) {
    return this.fetchUrl(
      'download',
      params.blockId,
      params.ossName,
      params.fileName || `${Date.now()}`,
      params.opt
    );
  }

  private getCacheKey(type: UrlType, ossName: string, fileName?: string, isShare?: boolean) {
    return `${type}${ossName}${fileName}${isShare || this.isShare}`;
  }

  /**
   * 清理过期的url
   */
  private tryClearCache(key: string) {
    const info = this.lruCache.get(key);
    if (info && info.expireTime <= Date.now()) {
      this.lruCache.delete(key);
      delete this.fetchers[key];
    }

    // 如果过期时长超过2小时，则可能是用户系统时间调整导致的错误，需求fix
    if (info && info.expireTime > Date.now() + 1000 * 1 * 60 * 60) {
      this.lruCache.delete(key);
    }
  }

  // 防止出现莫名其妙的链接失效问题
  public cleanup() {
    this.lruCache.clear();
  }
}
